import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAutoRefreshOptions {
    countdownSeconds?: number;
    enabled?: boolean;
    onRefresh?: () => void;
}

export function useAutoRefresh({
  countdownSeconds = 60,
  enabled = true,
  onRefresh
}: UseAutoRefreshOptions) {
  const [countDown, setCountDown] = useState(countdownSeconds);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // Track if this is the first load (refreshTrigger === 0) - used for showing skeleton only on initial load
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    const countdownTimer = setInterval(() => {
      setCountDown((prev) => {
        if (prev <= 1) {
          isInitialLoadRef.current = false;
          onRefresh?.();
          setRefreshTrigger((prev) => prev + 1);
          return countdownSeconds;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [enabled]);

  const manualRefresh = useCallback(() => {
    setCountDown(countdownSeconds);
    isInitialLoadRef.current = false;
    setRefreshTrigger((prev) => prev + 1);
    onRefresh?.();
  }, [countdownSeconds, onRefresh]);

  // Reset initial load flag when enabled changes (e.g., new truck selected)
  const resetInitialLoad = useCallback(() => {
    isInitialLoadRef.current = true;
  }, []);

  return {
    countDown,
    refreshTrigger,
    isInitialLoad: isInitialLoadRef.current,
    manualRefresh,
    resetInitialLoad,
  };
} 
